/**
 * AdminHub 初回起動〜ダッシュボード表示の簡易タイムライン計測
 * ブラウザコンソールに [Boot] ログを出す（UX は変更しない）
 */

const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
const spans = new Map();
const onceKeys = new Set();
const milestones = [];

function now() {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function absMs() {
  return now() - t0;
}

function fmt(ms) {
  return `${Math.round(ms)}ms`;
}

/**
 * @param {string} name
 * @param {string} [detail]
 */
export function bootMark(name, detail) {
  const abs = absMs();
  milestones.push({ name, abs, detail: detail || '' });
  const suffix = detail ? `  ${detail}` : '';
  console.log(`[Boot] ${fmt(abs)}  · ${name}${suffix}`);
}

/**
 * 同一キーは1回だけ（StrictMode 二重実行対策）
 * @param {string} name
 * @param {string} [detail]
 * @returns {boolean}
 */
export function bootMarkOnce(name, detail) {
  if (onceKeys.has(name)) return false;
  onceKeys.add(name);
  bootMark(name, detail);
  return true;
}

/**
 * @param {string} name
 */
export function bootStart(name) {
  // 同一名の未完了 span がある場合は上書きしない（再マウント時の 0ms 終了を防ぐ）
  if (!spans.has(name)) {
    spans.set(name, now());
    console.log(`[Boot] ${fmt(absMs())}  ▶ ${name}`);
  }
}

/**
 * @param {string} name
 * @param {string} [detail]
 * @returns {number} duration ms
 */
export function bootEnd(name, detail) {
  const start = spans.get(name);
  if (start == null) {
    // 対応する start が無い（stale インスタンス等）はログを出さない
    return 0;
  }
  spans.delete(name);
  const dur = now() - start;
  const abs = absMs();
  milestones.push({ name: `${name}:end`, abs, detail: detail || '', dur });
  const suffix = detail ? `  ${detail}` : '';
  console.log(`[Boot] ${fmt(abs)}  ■ ${name}  ${fmt(dur)}${suffix}`);
  return dur;
}

/**
 * 区間小計（t0 からの累計）
 * @param {string} label
 */
export function bootSubtotal(label) {
  console.log(`[Boot] ── ${label}  累計 ${fmt(absMs())} ──`);
}

/**
 * 最終サマリ（主要マイルストーンの再掲）
 * @param {string} [label]
 */
export function bootSummary(label = 'ダッシュボード表示完了') {
  const total = absMs();
  console.log(`[Boot] ===== ${label}  合計 ${fmt(total)} =====`);
  if (milestones.length === 0) return;
  const lines = milestones
    .filter((m) => !String(m.name).endsWith(':end') || m.dur != null)
    .slice(-12)
    .map((m) => {
      if (m.dur != null) return `  ${fmt(m.abs)}  ${m.name.replace(/:end$/, '')} (${fmt(m.dur)})`;
      return `  ${fmt(m.abs)}  ${m.name}`;
    });
  console.log(`[Boot] 直近:\n${lines.join('\n')}`);
}

/**
 * @param {string} name
 * @returns {boolean} true if this is the first start
 */
export function bootStartOnce(name) {
  const key = `start:${name}`;
  if (onceKeys.has(key)) return false;
  onceKeys.add(key);
  bootStart(name);
  return true;
}

/**
 * @param {string} name
 * @param {string} [detail]
 * @returns {number}
 */
export function bootEndOnce(name, detail) {
  const key = `end:${name}`;
  if (onceKeys.has(key)) return 0;
  onceKeys.add(key);
  return bootEnd(name, detail);
}
